import { useEffect, useState } from "react";
import { CheckCircleFilled, CloseCircleFilled, EditOutlined, PoweroffOutlined, SyncOutlined, UserOutlined } from "@ant-design/icons";
import { Button, Image, Modal, Popconfirm, Progress, message } from "antd";
import moment from "moment";

import StringUtils from "../../../../../../util/StringUtils";
import TenantApi from "../../../../../../api/TenantApi";
import TenantStatus from "../../../../../../components/ui-components/tenant-status/TenantStatus";
import UpdateTenantInformation from "./components/update-tenant/UpdateTenantInformation";
import { AdminPassword, AdminPasswordTips, AdminPasswordTitle, AdminPasswordWrapper, InlineBlock, PrefixIconWrapper, StatisticsCharts, StatisticsNumber, StatisticsTitle, StatisticsValue, TenantAddress, TenantAvatar, TenantIndustry, TenantInformationHeader, TenantInformationWrapper, TenantName, TenantNo, TenantOperation, TenantOperationButtons, TenantOperationDescription, TenantOperationTitle, TenantOperations, TenantRemarks, TenantStatistics, TenantStatisticsWrapper, TenantText, TenantTitleBar } from "./TenantInformation.styled";
import Api from "../../../../../../api/Api";
import ByteUtils from "../../../../../../util/ByteUtils";
import TenantUserApi from "../../../../../../api/TenantUserApi";

interface TenantInformationProps {
    tenantView: any
}

export const TenantInformation: React.FC<TenantInformationProps> = (props)=> {
    // api
    const [messageApi, contextHolder] = message.useMessage();
    let tenantApi = new TenantApi();
    let tenantUserApi = new TenantUserApi();

    // state
    let [openUpdateTenantInformationModal, setOpenUpdateTenantInformationModal] = useState(false);
    let [tenantView, setTenantView] = useState(props.tenantView);

    let [openResetAdminPasswordModal, setOpenResetAdminPasswordModal] = useState(false);
    let [adminPassword, setAdminPassword] = useState("");

    let onEditTenantInformation = ()=> {
        setOpenUpdateTenantInformationModal(true);
    };

    let onLoginTenantAdmin = async ()=> {
        let authorization = (await tenantUserApi.generateTenantAdminAuthorization(props.tenantView.tenant.id)).data.data;
        window.open('https://fusion.intcon-sys.com?t=' + authorization);
    };

    let onResetAdminPassword = async ()=> {
        let responseData = (await tenantApi.resetTenantAdminPassword(props.tenantView.tenant.id)).data.data;
        setOpenResetAdminPasswordModal(true);
        setAdminPassword(responseData.password);
    };

    let onReInitializeTenantDatabase = async ()=> {
        let responseData = (await tenantApi.reInitializeTenantDatabase(props.tenantView.tenant.id)).data.data;
        messageApi.open({
            type: 'success',
            content: '数据库初始化成功',
        });
    };

    let refreshTenantView = async ()=> {
        let tenantView = (await tenantApi.getTenantViewById(props.tenantView.tenant.id)).data.data;
        setTenantView(tenantView);
    };

    // utils
    let formatDateTime = (timestamp: number | string)=> {
        timestamp = Number(timestamp);
        return moment(timestamp).format('YYYY/MM/DD HH:mm:ss');
    }

    // render
    return (
        <TenantInformationWrapper>
            {contextHolder}
            <TenantInformationHeader>
                <TenantTitleBar>
                    <TenantAvatar>
                        <Image 
                            src={Api.URL_PREFIX + tenantView.tenant.avatar}
                            width="200px"
                            style={{borderRadius: '5px'}} />
                    </TenantAvatar>
                    
                    <TenantText>
                        <TenantName>{tenantView.tenant.name} <Button type='link' onClick={onEditTenantInformation}><EditOutlined />编辑</Button></TenantName>
                        <TenantNo><PrefixIconWrapper><span className="icon iconfont icon-cube"></span></PrefixIconWrapper><InlineBlock>编号：{tenantView.tenant.tenantNo}</InlineBlock></TenantNo>
                        <TenantIndustry><PrefixIconWrapper><span className="icon iconfont icon-company"></span></PrefixIconWrapper><InlineBlock>行业：{tenantView.tenant.industry}</InlineBlock></TenantIndustry>
                        <TenantAddress><PrefixIconWrapper><span className="icon iconfont icon-address"></span></PrefixIconWrapper><InlineBlock>地址：{tenantView.tenant.address}</InlineBlock></TenantAddress>
                        <TenantRemarks><PrefixIconWrapper><span className="icon iconfont icon-remarks"></span></PrefixIconWrapper><InlineBlock>备注：{tenantView.tenant.remarks}</InlineBlock></TenantRemarks>
                    </TenantText>
                </TenantTitleBar>
            </TenantInformationHeader>

            <TenantStatisticsWrapper>
                <TenantStatistics>
                    <StatisticsTitle>数据库</StatisticsTitle>
                    <StatisticsValue>数据：<StatisticsNumber style={{color: "#FF0000"}}>{ByteUtils.bytesToGB(tenantView.tenantDatabaseStatistics.dataSize).toFixed(2)}</StatisticsNumber>GB / 索引：<StatisticsNumber style={{color: "rgb(22,120,255)"}}>{ByteUtils.bytesToGB(tenantView.tenantDatabaseStatistics.indexSize).toFixed(2)}</StatisticsNumber>GB</StatisticsValue>
                    <StatisticsCharts>
                        <Progress percent={ByteUtils.bytesToGB(tenantView.tenantDatabaseStatistics.totalSize) / 10 * 100 } strokeColor="rgb(22,120,255)" success={{percent: ByteUtils.bytesToGB(tenantView.tenantDatabaseStatistics.dataSize) / 10 * 100, strokeColor: 'rgb(249, 125, 90)'}} showInfo={false} />
                    </StatisticsCharts>
                </TenantStatistics>

                <TenantStatistics>
                    <StatisticsTitle>设备数量</StatisticsTitle>
                    <StatisticsValue>总数：<StatisticsNumber>{tenantView.tenantNodeStatistics.total}</StatisticsNumber> / 在线：<StatisticsNumber style={{color: 'rgb(28, 178, 115)'}}>{tenantView.tenantNodeStatistics.online}</StatisticsNumber> / 离线：<StatisticsNumber style={{color: '#F97D5B'}}>{tenantView.tenantNodeStatistics.offline}</StatisticsNumber></StatisticsValue>
                    <StatisticsCharts>
                        <Progress percent={tenantView.tenantNodeStatistics.total > 0 ? tenantView.tenantNodeStatistics.online / tenantView.tenantNodeStatistics.total * 100 : 0} strokeColor="rgb(28, 178, 115)" showInfo={false} />
                    </StatisticsCharts>
                </TenantStatistics>

                <TenantStatistics>
                    <StatisticsTitle>短信使用量</StatisticsTitle>
                    <StatisticsValue>短信：<StatisticsNumber>10</StatisticsNumber>/100</StatisticsValue>
                    <StatisticsCharts>
                        <Progress percent={50} showInfo={false} />
                    </StatisticsCharts>
                </TenantStatistics>
            </TenantStatisticsWrapper>

            <TenantOperations>
                <TenantOperation>
                    <TenantOperationTitle>如何管理租户的设备和数据</TenantOperationTitle>
                    <TenantOperationDescription>点击下方链接以管理员身份进入到租户管理控制台页面，租户之间的数据是相互隔离的，您可以放心对租户的设备和数据进行管理。</TenantOperationDescription>
                    <TenantOperationButtons>
                        <Button type='link' onClick={onLoginTenantAdmin}><UserOutlined />登录租户控制台</Button>
                    </TenantOperationButtons>
                </TenantOperation>
                <TenantOperation>
                    <TenantOperationTitle>忘记了管理员密码怎么办？</TenantOperationTitle>
                    <TenantOperationDescription>如忘记租户管理员密码首次创建租户，可以在此对管理员密码进行重置，重置后旧密码将丢失</TenantOperationDescription>
                    <TenantOperationButtons>
                    <Popconfirm
                            title="重置管理员密码"
                            description={ <div style={{paddingTop: "10px", paddingBottom: '10px'}}>此操作将重置管理员密码，确认要重置么?</div> }
                            onConfirm={()=> onResetAdminPassword()}
                            okText="确定"
                            cancelText="取消"
                        > 
                        <Button type='link'><SyncOutlined />重置管理员密码</Button>
                    </Popconfirm>
                    </TenantOperationButtons>
                </TenantOperation>
                <TenantOperation>
                    <TenantOperationTitle>租户数据库初始化失败？</TenantOperationTitle>
                    <TenantOperationDescription>你可以对租户重新执行数据库初始化，数据库初始化不会影响现有数据</TenantOperationDescription>
                    <TenantOperationButtons>
                        <Popconfirm
                            title="初始化数据库"
                            description={ <div style={{paddingTop: "10px", paddingBottom: '10px'}}>此操作将初始化租户数据库存，确认执行初始化操作么?</div> }
                            onConfirm={()=> onReInitializeTenantDatabase()}
                            okText="确定"
                            cancelText="取消"
                        > 
                            <Button type='link'><SyncOutlined />初始化数据库</Button>
                        </Popconfirm>
                    </TenantOperationButtons>
                </TenantOperation>
            </TenantOperations>

            <Modal open={openUpdateTenantInformationModal} 
                afterClose={()=> setOpenUpdateTenantInformationModal(false)} 
                footer={null} 
                closable={false}
                destroyOnClose={true}
                width={1000}
                >
                <UpdateTenantInformation
                    tenantView={tenantView}
                    onClose={()=> setOpenUpdateTenantInformationModal(false)} 
                    onComplete={()=> refreshTenantView()}/>
            </Modal>

            <Modal open={openResetAdminPasswordModal} 
                title="重置管理员密码"
                cancelButtonProps={{style: {display: 'none'}}}
                afterClose={()=> setOpenResetAdminPasswordModal(false)} 
                onOk={()=> setOpenResetAdminPasswordModal(false)}
                destroyOnClose={true}
                width={500} >
                <AdminPasswordWrapper>
                    <AdminPasswordTitle>管理员密码已重置为：</AdminPasswordTitle>
                    <AdminPassword>{adminPassword}</AdminPassword>
                    <AdminPasswordTips>请复制上述密码，对话框关闭后将无法再次查看<br /> 如误关闭请再次执行密码重置操作</AdminPasswordTips>
                </AdminPasswordWrapper>
            </Modal>
        </TenantInformationWrapper>
    )
}

export default TenantInformation;
